import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useHistory } from 'react-router-dom';

// COMPONENTS
import DisplayDateTime from '../../components/DisplayDateTime';
import Button from '../../components/Button';
import CircleImage from '../../components/CircleImage';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';
import { ReactComponent as AnonymousIcon } from '../../assets/svg/anonymous-icon.svg';

// TYPES
import { FeedbackCreateCommand, DomainFeedbackCategory } from '../../api/models';
import { FeedbackFormTypes } from './FeedbackSubmit';

interface StepTwoTypes {
	feedbackFormData: FeedbackFormTypes;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;

	isLoading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepTwo: React.FC<StepTwoTypes> = ({ feedbackFormData, setActiveStep, isLoading, setIsLoading }) => {
	const history = useHistory();

	const user = useAppSelector(({ user }) => user);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all Feedback Categories from Redux store
	const feedbackCategories = useAppSelector((state) => state.lists.feedbackCategories);

	// Error state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	// Logic to find the role of the submitter from store.
	const userActiveProfileId = user.activeProfileId;
	const allProfiles = user.profiles;
	const activeProfile = allProfiles?.find((item) => item.id === userActiveProfileId);
	const feedbackSubmissionRoleName = activeProfile?.roleName;

	const {
		subject,
		occurrence,
		category,
		summary,
		createAttachments,
		anonymously,
		anonymousContact,
		isResponseRequired,
		attachments
	} = feedbackFormData;

	const createFeedback = async (feedbackFormData: FeedbackCreateCommand) => {
		if (feedbackFormData !== undefined) {
			setIsLoading(true);

			const response = await apibridge.createFeedback(feedbackFormData);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				history.push('/feedback');

				useUpdateTotalNotification();
			}
		}
	};

	const displayCategory = (category?: DomainFeedbackCategory) => {
		if (category) {
			const currentCategory = feedbackCategories?.find((item) => item.value === category);
			return currentCategory?.label;
		}
	};

	return (
		<div>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					<h2 className="general-header">Please review your feedback before submitting.</h2>

					<p className="sub-grey-text mbtom-48">
						The leadership team for this organisation will be notified of your feedback. If you are submitting this
						feedback anonymously, the leadership team cannot see who has submitted the feedback.
					</p>

					<div className="general-form-layout-wrapper">
						<div className="complaints-detail">
							<div className="detail-block">
								<h2 className="detail-heading">Subject</h2>

								<p className="detail-info">{subject}</p>
							</div>

							<div className="detail-block">
								<h2 className="detail-heading">Date and time of relevant event</h2>

								<time className="detail-info">
									<DisplayDateTime>{occurrence}</DisplayDateTime>
								</time>
							</div>

							<div className="detail-block">
								<h2 className="detail-heading">Category</h2>

								<p className="detail-info">{displayCategory(category)}</p>
							</div>

							<div className="detail-block">
								<h2 className="detail-heading">Feedback Summary</h2>

								<p className="detail-info">{summary}</p>
							</div>

							{attachments && attachments.length > 0 && (
								<div className="detail-block">
									<h2 className="detail-heading">Attachments</h2>

									<ul>
										{attachments.map((item, index) => {
											return (
												<li key={index}>
													<div className="attachments-download-link">
														<span className="link-name">{item.displayFileName}</span>
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							)}

							<div className="detail-block">
								<h3 className="detail-heading mbtom-14">Feedback submission by</h3>

								<ul className="mbtom-14">
									{anonymously ? (
										<li className="row mbtom-14">
											<div className="col-1 pdr-0 flex-center">
												<AnonymousIcon />
											</div>

											<div className="col-3 people-name">Anonymous</div>
										</li>
									) : (
										<li className="row mbtom-14">
											<div className="col-1 pdr-0 flex-center">
												<CircleImage src={user.profileImage} size={40} />
											</div>

											<div className="col-4 people-name">
												{user.firstName} {user.lastName}
											</div>

											<div className="col-7 people-role">{feedbackSubmissionRoleName}</div>
										</li>
									)}
								</ul>
							</div>

							<div className="detail-block">
								<h2 className="detail-heading">Is the feedback required to be anonymous?</h2>

								<p className="detail-info">{anonymously ? 'Yes' : 'No'}</p>
							</div>

							{anonymously && anonymousContact && (
								<div className="detail-block">
									<h2 className="detail-heading">Anonymous contact detail</h2>

									<p className="detail-info">{anonymousContact}</p>
								</div>
							)}

							<div className="detail-block">
								<h2 className="detail-heading">Is a response required?</h2>

								<p className="detail-info">{isResponseRequired ? 'Yes' : 'No'}</p>
							</div>
						</div>
						<div className="general-form-guide">
							<QuestionMarkIcon />
							<h2 className="guide-heading">When to submit Feedback</h2>

							<p className="guide-content">
								Feedback is an important way for organisations to improve safety practices. You may have observed a
								safety risk or have a suggestion about what could be done better to improve children’s safety.
								ChildAware organisations welcome feedback as part of their commitment to continuous improvement. If your
								concern is of a serious nature or relates to a specific incident, it may be appropriate to submit
								a&nbsp;
								<Link className="link" to="/complaints-submit">
									Complaint
								</Link>
								&nbsp; instead.
							</p>

							<p className="guide-content">
								<strong>Note:</strong> If a child is in immediate danger, call police or emergency services on 000.
							</p>
						</div>
					</div>

					<div className="general-divider"></div>

					<div className="form-two-button-group">
						<Button
							buttonIcon={<ArrowLeftIcon className="link-icon" />}
							className="btn-secondary edit-btn"
							onClick={() => setActiveStep('step1')}
						>
							Edit details
						</Button>

						{isOrganisationActive && (
							<Button className="btn-primary submit-btn" onClick={() => createFeedback(feedbackFormData)}>
								Submit Feedback
							</Button>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default StepTwo;
